import { DataAccess } from "../dataaccess/data.access";
import { Cache, Invalidate } from "../decorators/cache.decorator";
import { S25Util } from "../util/s25-util";
import { Proto } from "../pojo/Proto";
import Unwrap = Proto.Unwrap;
import { DeepPartial } from "../pojo/Util";
import { AccessLevel } from "../pojo/Fls";
import ISODateString = Proto.ISODateString;
import { RecordType } from "../modules/s25-event/ProfileI";
import { EventPerm } from "./avail.service";
import { StandardSchedule } from "./standard.schedule.service";

export class GridsService {
    @Cache({ targetName: "GridsService" })
    public static getList() {
        const url = "/grids/list";
        const injected = DataAccess.injectCaller(url, "GridsService.getGrids");
        return S25Util.Maybe(DataAccess.get<GridsListResponse>(injected));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static delete(id: number) {
        const url = `/grids/grid?id=${id}`;
        const injected = DataAccess.injectCaller(url, "GridsService.delete");
        return S25Util.Maybe(DataAccess.delete(injected));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static create(data: DeepPartial<GridListItem>) {
        const url = `/grids/grid`;
        const injected = DataAccess.injectCaller(url, "GridsService.delete");
        return S25Util.Maybe(DataAccess.post<GridListItem>(injected, data));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static update(data: DeepPartial<GridListItem> & Pick<GridListItem, "id">) {
        const url = `/grids/grid`;
        const injected = DataAccess.injectCaller(url, "GridsService.update");
        return S25Util.Maybe(DataAccess.patch<GridListItem>(injected, data));
    }

    public static getData(id: number) {
        const url = `/grids/grid/data.json?id=${id}&version=2`;
        const injected = DataAccess.injectCaller(url, "GridService.getData");
        return S25Util.Result(DataAccess.get<GridDataResponse>(injected));
    }

    @Invalidate({ serviceName: "GridsService", methodName: "getList" })
    public static setShared(id: number, users: number[]) {
        const url = `/grids/grid/share?id=${id}`;
        const injected = DataAccess.injectCaller(url, "GridService.setShared");
        return S25Util.Result(DataAccess.put<never>(injected, users));
    }

    public static moveItems(gridId: number, changes: GridItemChange[]) {
        const url = `/grids/grid/item/move.json?gridId=${gridId}`;
        const injected = DataAccess.injectCaller(url, "GridService.moveItems");
        return S25Util.Result(DataAccess.post<never>(injected, changes));
    }

    public static getChanges(gridId: number) {
        const url = `/grids/grid/item/changes.json?gridId=${gridId}`;
        const injected = DataAccess.injectCaller(url, "GridService.getChanges");
        return S25Util.Result(DataAccess.get<GridItemChange[]>(injected));
    }

    public static clearChanges(gridId: number) {
        const url = `/grids/grid/item/changes.json?gridId=${gridId}`;
        const injected = DataAccess.injectCaller(url, "GridService.clearChanges");
        return S25Util.Result(DataAccess.delete<void>(injected));
    }

    // TODO: Make sticky?
    public static async getPreferences() {
        return {
            startHour: 7,
            endHour: 17,
            dows: ["MWF", "TR"],
        };
    }
}

export type GridListItem = Unwrap<Awaited<ReturnType<typeof GridsService.getList>>>[number];

type GridsListResponse = {
    id: number;
    name: string;
    eventQuery: { queryId: number; queryName: string };
    type: string;
    editable: boolean;
    owner: number;
    isOwner: boolean;
    sharedWith: { grid: number; contId: number; name: string }[];
    defaultSchedule?: number;
}[];

export type GridDataResponse = {
    err_msg?: string;
    events: GridEvent[];
    changes: GridItemChange[];
    spaceAp: Record<number, EventPerm>;
    resourceAp: Record<number, EventPerm>;
    terms: Record<string, GridDataTerm>;
    defaultSchedule: StandardSchedule[];
};

export type GridEvent = {
    id: number;
    name: string;
    title: string;
    locator: Proto.EventReference;
    alienUid: string;
    ols: AccessLevel;
    setup: number; // Minutes
    takedown: number; // Minutes
    primaryOrganization?: { id: number; name: string; ols: AccessLevel };
    roles: { id: number; role: number; firstName: string; familyName: string }[];
    attributes: { id: number; value: string }[];
    profiles: GridProfile[];
};

export type GridProfile = {
    id: number;
    name: string;
    expectedHeadCount: number;
    registeredHeadCount: number;
    recType: RecordType;
    profileCode: string;
    alienUID: string;
    start: ISODateString;
    end: ISODateString;
    pre: number; // Minutes
    post: number; // Minutes
    boundProfiles: number[];
    featurePreferences: { id: number; name: string }[];
    reservations: GridReservation[];
};

export type GridReservation = {
    id: number;
    start: ISODateString;
    end: ISODateString;
    commentId: number;
    locations: GridReservationLocation[];
    resources: GridReservationResource[];
};

export type GridReservationLocation = {
    id: number;
    name: string;
    shared: boolean;
    instructionsId: number;
    layoutId: number;
    attendance: number;
    ols: AccessLevel;
};

export type GridReservationResource = {
    id: number;
    quantity: number;
    instructionId: number;
};

export type GridItemChange = {
    gridId: number;
    eventId: number;
    profileId: number;
    pattern: string;
    startTime: ISODateString;
    endTime: ISODateString;
};

export type GridDataTerm = {
    cid: number;
    term_code: string;
    term_desc: string;
    start_dt: ISODateString;
    end_dt: ISODateString;
};
